import { useRef, useState } from 'react'
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { useParams } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import {
  useCollectionPins,
  useOwnedCollections,
  useSharedCollections,
  useUnpin,
  useEditPin,
  useMovePinUp,
  useMovePinDown,
} from '~/api/hooks/useCollections'
import { usePageControls } from '~/api/hooks/useCharts'
import { labelFor } from '~/components/ReviewPage/kpiLabel'
import { ChartFromPin } from '~/components/Charts/ChartFromPin'
import { PageHeader } from '~/components/Navigation/PageHeader'
import { PageExportButtons } from '~/components/Export/PageExportButtons'
import { CollectionDataExportButton } from '~/components/Export/CollectionDataExportButton'
import { ExportButton } from '~/components/CollectionPin/ExportButton'
import { PinEditDialog } from '~/components/Collections/PinEditDialog'
import { errorString } from '~/errors/handleApiError'
import { buildExportFilenameBase } from '~/util/export/filename'
import type { CollectionPinResult } from '~/api/endpoints/collections'
import { DIALOGS } from '~/components/Charts/chrome/chartChrome'

export function CollectionItem() {
  const { collectionId = null } = useParams<{ collectionId: string }>()
  const { user } = useAuth0()
  const myId = user?.sub
  const pins = useCollectionPins(collectionId)
  const owned = useOwnedCollections()
  const shared = useSharedCollections()
  const currentCollection =
    (owned.data ?? []).find((c) => c.id === collectionId) ??
    (shared.data ?? []).find((c) => c.id === collectionId)
  const sharedView = currentCollection ? currentCollection.user_id !== myId : false

  // One controls fetch resolves ALL pin titles: kpi_metadata is registry-wide (not
  // fact-filtered), so the first pin's fact_table is as good as any. null = no pins yet.
  const labelFact =
    (pins.data ?? []).map((r) => ('pin' in r ? r.pin.walk_call.fact_table : null)).find(Boolean) ??
    null
  const labelControls = usePageControls(labelFact)

  const unpin = useUnpin()
  const edit = useEditPin()
  const moveUp = useMovePinUp()
  const moveDown = useMovePinDown()

  // 3-dot menu anchor (per-pin) — track by pin id
  const [menuAnchor, setMenuAnchor] = useState<{ id: string; el: HTMLElement } | null>(null)

  // Pin Edit Dialog target
  const [editTarget, setEditTarget] = useState<CollectionPinResult | null>(null)

  // Page-level ref for image / pdf export
  const pageRef = useRef<HTMLDivElement>(null)

  const validPins = (pins.data ?? []).flatMap((r) => ('pin' in r ? [r.pin] : []))
  const filenameBase = buildExportFilenameBase({
    scope: 'collection',
    name: currentCollection?.name ?? 'untitled',
  })

  const closeMenu = () => {
    setMenuAnchor(null)
    // Clear any prior mutation error so the next action's outcome is unambiguous;
    // if the next click triggers a new mutation, its own error replaces the cleared state.
    moveUp.reset()
    moveDown.reset()
    unpin.reset()
  }

  return (
    <Box sx={{ p: 4, maxWidth: 1400, mx: 'auto' }} ref={pageRef}>
      <PageHeader
        title={currentCollection?.name ?? DIALOGS.collection}
        actions={
          currentCollection ? (
            <Stack direction="row" spacing={1} alignItems="center" data-export-skip="true">
              <PageExportButtons
                target={() => pageRef.current}
                filenameBase={filenameBase}
                disabled={!pins.data || pins.data.length === 0}
              />
              <CollectionDataExportButton
                pins={validPins}
                filenameBase={filenameBase}
                disabled={!pins.data || pins.data.length === 0}
              />
              <ExportButton collectionName={currentCollection.name} pins={validPins} />
            </Stack>
          ) : null
        }
      />
      {sharedView && currentCollection ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {DIALOGS.sharedBy(currentCollection.owner_email ?? currentCollection.user_id)}
        </Typography>
      ) : null}
      {pins.isLoading ? <CircularProgress /> : null}
      {pins.isError ? <Alert severity="error">{DIALOGS.couldNotLoadPins}</Alert> : null}
      {moveUp.error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => moveUp.reset()}>
          {errorString(moveUp.error) ?? DIALOGS.couldNotMoveUp}
        </Alert>
      ) : null}
      {moveDown.error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => moveDown.reset()}>
          {errorString(moveDown.error) ?? DIALOGS.couldNotMoveDown}
        </Alert>
      ) : null}
      {unpin.error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => unpin.reset()}>
          {errorString(unpin.error) ?? DIALOGS.couldNotRemovePin}
        </Alert>
      ) : null}

      <Stack spacing={3}>
        {(pins.data ?? []).map((result, index, all) => {
          const isFirst = index === 0
          const isLast = index === all.length - 1
          const description = 'pin' in result ? (result.row.custom_notes ?? '').trim() : ''
          const titleText =
            'pin' in result
              ? (result.pin.render_hint.title ?? labelFor(labelControls, result.pin.walk_call.kpi))
              : '⚠ Pin error'

          return (
            <Box
              key={result.row.id}
              sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}
            >
              {description ? (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  {description}
                </Typography>
              ) : null}

              <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="subtitle1" sx={{ flex: 1 }}>
                  {`${index + 1}. ${titleText}`}
                </Typography>
                {!sharedView && (
                  <IconButton
                    size="small"
                    aria-label={DIALOGS.pinActionsAria(titleText)}
                    onClick={(e) => setMenuAnchor({ id: result.row.id, el: e.currentTarget })}
                  >
                    <MoreVertIcon />
                  </IconButton>
                )}
              </Stack>

              {'pin' in result ? (
                <ChartFromPin pin={result.pin} />
              ) : (
                <Alert severity="warning">
                  {DIALOGS.pinLoadFailed}
                  <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                    {result.error}
                  </Typography>
                </Alert>
              )}

              {!sharedView && (
                <Menu
                  open={menuAnchor?.id === result.row.id}
                  anchorEl={menuAnchor?.el}
                  onClose={closeMenu}
                >
                  <MenuItem
                    disabled={isFirst}
                    aria-disabled={isFirst}
                    onClick={() => {
                      closeMenu()
                      if (collectionId) moveUp.mutate({ pinId: result.row.id, collectionId })
                    }}
                  >
                    {DIALOGS.moveUp}
                  </MenuItem>
                  <MenuItem
                    disabled={isLast}
                    aria-disabled={isLast}
                    onClick={() => {
                      closeMenu()
                      if (collectionId) moveDown.mutate({ pinId: result.row.id, collectionId })
                    }}
                  >
                    {DIALOGS.moveDown}
                  </MenuItem>
                  <MenuItem
                    disabled={!('pin' in result)}
                    onClick={() => {
                      closeMenu()
                      if ('pin' in result) setEditTarget(result)
                    }}
                  >
                    {DIALOGS.pinEditMenuLabel}
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      closeMenu()
                      if (collectionId) unpin.mutate({ pinId: result.row.id, collectionId })
                    }}
                  >
                    {DIALOGS.removeFromCollection}
                  </MenuItem>
                </Menu>
              )}
            </Box>
          )
        })}
      </Stack>

      <PinEditDialog
        open={editTarget !== null}
        initialTitle={
          editTarget && 'pin' in editTarget ? (editTarget.pin.render_hint.title ?? '') : ''
        }
        initialDescription={editTarget?.row.custom_notes ?? ''}
        isSubmitting={edit.isPending}
        error={errorString(edit.error)}
        onClose={() => {
          setEditTarget(null)
          edit.reset()
        }}
        onSubmit={async ({ title, description }) => {
          if (!editTarget || !('pin' in editTarget) || !collectionId) return
          const nextPin = {
            ...editTarget.pin,
            render_hint: { ...editTarget.pin.render_hint, title },
          }
          await edit.mutateAsync({
            pinId: editTarget.row.id,
            collectionId,
            pin: nextPin,
            customNotes: description === '' ? null : description,
          })
          setEditTarget(null)
        }}
      />
    </Box>
  )
}
